import { useState, useEffect, useMemo, useRef } from '@wordpress/element';
import { useSelect, useDispatch } from '@wordpress/data';
import { __, sprintf }            from '@wordpress/i18n';
import { STORE_NAME }             from '../../../store';
import { fetchCategories }        from '../../../utils/api';
import { classNames }             from '../../../utils/helpers';

export default function CategoryFilter( { layout = 'list', multiSelect = true, showCount = true, categoryRoot = [], categoryRootIncludeChildren = true, hideEmptyCategories = false } ) {
	const [ categories, setCategories ] = useState( [] );
	const [ open, setOpen ]             = useState( false );
	const [ loading, setLoading ]       = useState( true );

	const selected = useSelect( ( s ) => s( STORE_NAME ).getCategories(), [] );
	const { setCategories: dispatchCategories, setFilter: dispatchFilter, setCategoryNames } = useDispatch( STORE_NAME );

	const [ initialCats ] = useState( () => {
		const el = document.querySelector( '[data-block="mwsb/product-filters"][data-initial-categories]' );
		if ( ! el ) return null;
		const data = JSON.parse( el.getAttribute( 'data-initial-categories' ) || 'null' );
		el.removeAttribute( 'data-initial-categories' );
		return data;
	} );

	const [ localSelected, setLocalSelected ] = useState( selected );
	const pendingRef  = useRef( selected );
	const debounceRef = useRef( null );

	useEffect( () => {
		pendingRef.current = selected;
		setLocalSelected( selected );
	}, [ selected ] );

	useEffect( () => {
		dispatchFilter( 'includeChildren', categoryRootIncludeChildren );
	}, [ categoryRootIncludeChildren, dispatchFilter ] );

	useEffect( () => {
		if ( initialCats ) {
			setCategories( initialCats );
			const names = {};
			initialCats.forEach( ( c ) => { names[ c.slug ] = c.name; } );
			setCategoryNames( names );
			setLoading( false );
			return;
		}
		fetchCategories()
			.then( ( cats ) => {
				setCategories( cats );
				const names = {};
				cats.forEach( ( c ) => { names[ c.slug ] = c.name; } );
				setCategoryNames( names );
			} )
			.catch( () => {} )
			.finally( () => setLoading( false ) );
	}, [] ); // eslint-disable-line react-hooks/exhaustive-deps

	const baseVisibleCategories = useMemo( () => {
		const roots = Array.isArray( categoryRoot ) ? categoryRoot : ( categoryRoot ? [ categoryRoot ] : [] );

		if ( ! roots.length ) {
			return categories;
		}

		const rootCats = categories.filter( ( cat ) => roots.includes( cat.slug ) );
		if ( ! rootCats.length ) {
			return categories;
		}

		if ( categoryRootIncludeChildren ) {
			const childrenMap = categories.reduce( ( map, cat ) => {
				if ( ! map[ cat.parent ] ) {
					map[ cat.parent ] = [];
				}
				map[ cat.parent ].push( cat );
				return map;
			}, {} );

			const ids = new Set();
			const collect = ( id ) => {
				if ( ids.has( id ) ) {
					return;
				}
				ids.add( id );
				( childrenMap[ id ] || [] ).forEach( ( child ) => collect( child.id ) );
			};

			rootCats.forEach( ( root ) => collect( root.id ) );
			return categories.filter( ( cat ) => ids.has( cat.id ) );
		}

		return rootCats;
	}, [ categories, categoryRoot, categoryRootIncludeChildren ] );

	const visibleCategories = useMemo( () => {
		return hideEmptyCategories
			? baseVisibleCategories.filter( ( cat ) => cat.count > 0 )
			: baseVisibleCategories;
	}, [ baseVisibleCategories, hideEmptyCategories ] );

	function toggleCategory( slug ) {
		const current = pendingRef.current;
		const next = multiSelect
			? ( current.includes( slug ) ? current.filter( ( s ) => s !== slug ) : [ ...current, slug ] )
			: ( current[ 0 ] === slug ? [] : [ slug ] );
		pendingRef.current = next;
		setLocalSelected( next );
		clearTimeout( debounceRef.current );
		debounceRef.current = setTimeout( () => dispatchCategories( pendingRef.current ), 150 );
	}

	if ( loading ) return <div className="mwsb-filter mwsb-filter--skeleton" />;
	if ( ! visibleCategories.length ) return null;

	if ( layout === 'dropdown' ) {
		return (
			<div className="mwsb-filter mwsb-filter--categories mwsb-filter--dropdown">
				<button
					className={ classNames( 'mwsb-dropdown-trigger', open && 'is-open' ) }
					onClick={ () => setOpen( ( o ) => ! o ) }
					aria-expanded={ open }
				>
					<span>{ localSelected.length ? sprintf( __( '%d selected', 'mwsb' ), localSelected.length ) : __( 'All Categories', 'mwsb' ) }</span>
					<svg viewBox="0 0 24 24" width="14" height="14"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>
				</button>
				{ open && (
					<ul className="mwsb-dropdown-list">
						{ visibleCategories.map( ( cat ) => (
							<CategoryItem
								key={ cat.id }
								cat={ cat }
								selected={ localSelected }
								toggleCategory={ toggleCategory }
								showCount={ showCount }
							/>
						) ) }
					</ul>
				) }
			</div>
		);
	}

	if ( layout === 'pills' ) {
		return (
			<div className="mwsb-filter mwsb-filter--categories mwsb-filter--pills">
				{ visibleCategories.map( ( cat ) => (
					<button
						key={ cat.id }
						className={ classNames( 'mwsb-pill', localSelected.includes( cat.slug ) && 'is-active' ) }
						onClick={ () => toggleCategory( cat.slug ) }
					>
						{ cat.name }
						{ showCount && <span className="mwsb-pill__count">{ cat.count }</span> }
					</button>
				) ) }
			</div>
		);
	}

	if ( layout === 'accordion' ) {
		return (
			<div className="mwsb-filter mwsb-filter--categories mwsb-filter--accordion">
				<AccordionCategories
					categories={ visibleCategories }
					selected={ localSelected }
					toggleCategory={ toggleCategory }
					showCount={ showCount }
				/>
			</div>
		);
	}

	return (
		<div className="mwsb-filter mwsb-filter--categories mwsb-filter--list">
			<ul className="mwsb-category-list">
				{ visibleCategories.map( ( cat ) => (
					<CategoryItem
						key={ cat.id }
						cat={ cat }
						selected={ localSelected }
						toggleCategory={ toggleCategory }
						showCount={ showCount }
					/>
				) ) }
			</ul>
		</div>
	);
}

function CategoryItem( { cat, selected, toggleCategory, showCount } ) {
	const isActive = selected.includes( cat.slug );
	return (
		<li className={ classNames( 'mwsb-category-item', isActive && 'is-active' ) }>
			<label className="mwsb-category-label">
				<input
					type="checkbox"
					className="mwsb-category-checkbox"
					checked={ isActive }
					onChange={ () => toggleCategory( cat.slug ) }
				/>
				<span className="mwsb-category-name">{ cat.name }</span>
				{ showCount && <span className="mwsb-category-count">{ cat.count }</span> }
			</label>
		</li>
	);
}

function AccordionCategories( { categories, selected, toggleCategory, showCount } ) {
	const [ expanded, setExpanded ] = useState( true );

	return (
		<>
			<button
				className={ classNames( 'mwsb-accordion-trigger', expanded && 'is-open' ) }
				onClick={ () => setExpanded( ( e ) => ! e ) }
			>
				{ __( 'Categories', 'mwsb' ) }
				<svg viewBox="0 0 24 24" width="14" height="14"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>
			</button>
			{ expanded && (
				<ul className="mwsb-accordion-content mwsb-category-list">
					{ categories.map( ( cat ) => (
						<CategoryItem
							key={ cat.id }
							cat={ cat }
							selected={ selected }
							toggleCategory={ toggleCategory }
							showCount={ showCount }
						/>
					) ) }
				</ul>
			) }
		</>
	);
}
